import { useCallback, useState, useEffect, useRef } from "react";
import { useUploadMedia } from "../../lib/services/media-service";
import { useToken } from "../../lib/context/auth-context";
import Spinner from "./Spinner";
import Skeleton from "react-loading-skeleton";
import ImageComponent from "./ImageComponent";
import { retryArrow, uploadIcon, trashCan } from "../../assets";
import MediaViewer from "./MediaViewer";

const MediaThumbnail = ({
  document,
  index,
  onDelete,
  deleteIcon,
  retryIcon,
  onRetry,
  allProcessed,
  setShowMediaViewer,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  return (
    <div
      className="media-case"
      onClick={() => {
        if (!allProcessed) return;
        setShowMediaViewer(true);
      }}
    >
      <div className="media-thumbnail">
        <div className="media-img-container">
          {document.isUploading ? (
            <div className="spinner-case-small">
              <Spinner type="small" />
            </div>
          ) : document.error ? (
            <div className="error-placeholder">
              <p>Upload failed</p>
              <button
                className="icon retryIcon"
                onClick={() => onRetry(document)}
              >
                {retryIcon}
              </button>
            </div>
          ) : (
            <div className="media-thumb">
              {!imageLoaded && <Skeleton height={300} width={300} />}
              <ImageComponent
                alt={document.mediaName || `Media ${index + 1}`}
                src={document.mediaThumb}
                className="media-img"
                onLoad={() => setImageLoaded(true)}
                style={imageLoaded ? {} : { display: "none" }}
              />
            </div>
          )}
        </div>
        <div className="text-div">
          <p>{document.mediaName}</p>
          {!document.isUploading && (
            <button
              type="button"
              className="icon deleteIcon"
              onClick={(e) => {
                {
                  e.stopPropagation();
                  onDelete(index);
                }
              }}
            >
              {deleteIcon}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MediaUploader = ({
  onSuccess,
  setError,
  setIsUploading,
  supportedFiles,
  finishedUploading,
  allowMultipleUpload,
  destination,
  presetDocuments,
  mediaThumbnailClass = "media-container",
  mediaButtonClass = "media-input",
  uploadText = "Upload Files",
}) => {
  destination = destination || "attachments";

  const [showMediaViewer, setShowMediaViewer] = useState(false);

  const { token } = useToken();

  const formatUploadedMedia = (uploadedUrl) => {
    const media = uploadedUrl.response.media;

    return {
      mediaThumb: media.thumb,
      mediaUrl: media.url,
      mediaSize: media.size,
      mediaExt: media.ext,
      mediaHeight: media.height,
      mediaWidth: media.width,
      mediaName: media.file_name,
      mediaTime: media.timestamp,
      mediaType: media.media_type,
    };
  };

  const [uploadedDocuments, setUploadedDocuments] = useState(
    presetDocuments || []
  );

  const [hasTriggered, setHasTriggered] = useState(false);
  const prevLengthRef = useRef(0);

  const { mutateAsync: uploadMedia } = useUploadMedia({
    onError: (err) =>
      function () {
        if (!allowMultipleUpload) {
          setError?.(err.message);
        }
      },
  });

  useEffect(() => {
    if (finishedUploading) {
      setUploadedDocuments([]);
    }
  }, [finishedUploading]);

  const handleSelectMedia = useCallback((e) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    if (!allowMultipleUpload) {
      setError?.("");
    }

    const fileArray = Array.from(files);

    const selectedFiles = allowMultipleUpload ? fileArray : [fileArray[0]];

    selectedFiles.forEach((file) => {
      const tempId = `${file.name}-${Date.now()}`;

      const dummyFile = {
        mediaName: file.name,
        mediaThumb: "",
        isUploading: true,
        tempId,
        originalFile: file,
      };

      setUploadedDocuments((prev) =>
        allowMultipleUpload ? [...prev, dummyFile] : [dummyFile]
      );

      uploadSingleMedia(file, tempId);
    });
    e.target.value = null;
  }, []);

  const uploadSingleMedia = (file, tempId) => {
    setIsUploading?.(true);
    uploadMedia({
      file,
      token,
      folder: destination,
    })
      .then((uploadedData) => {
        const formatted = formatUploadedMedia(uploadedData);
        setUploadedDocuments((prev) =>
          prev.map((doc) => (doc.tempId === tempId ? formatted : doc))
        );
      })
      .catch((err) => {
        setUploadedDocuments((prev) =>
          prev.map((doc) =>
            doc.tempId === tempId
              ? {
                  ...doc,
                  isUploading: false,
                  error: true,
                  mediaName: file.name || "Upload failed",
                }
              : doc
          )
        );
      })
      .finally(() => {
        setUploadedDocuments((prev) => {
          const allDone = prev.every((d) => !d.isUploading);
          if (allDone) setIsUploading?.(false);
          return prev;
        });
      });
  };

  const handleRetryUpload = (document) => {
    if (document.isUploading) return;
    const { originalFile, tempId } = document;
    if (!originalFile || !tempId) return;

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        doc.tempId === tempId
          ? { ...doc, isUploading: true, error: false }
          : doc
      )
    );

    uploadSingleMedia(originalFile, tempId);
  };

  const handleDelete = useCallback((indexToDelete) => {
    setHasTriggered(false);
    setUploadedDocuments((prev) =>
      prev.filter((_, index) => index !== indexToDelete)
    );
  }, []);
  const allProcessed = uploadedDocuments.every(
    (doc) => doc.isUploading === false || !doc.isUploading
  );

  useEffect(() => {
    if (uploadedDocuments.length > 0 && allProcessed && !hasTriggered) {
      const successfulUploads = uploadedDocuments.filter(
        (doc) => doc.mediaUrl && !doc.error
      );

      if (
        successfulUploads.length > 0 &&
        successfulUploads.length === uploadedDocuments.length
      ) {
        onSuccess?.(successfulUploads);
        setHasTriggered(true);
      }
    }
  }, [uploadedDocuments, hasTriggered, onSuccess]);

  useEffect(() => {
    if (uploadedDocuments.length > prevLengthRef.current) {
      setHasTriggered(false);
    }
    prevLengthRef.current = uploadedDocuments.length;
  }, [uploadedDocuments]);

  useEffect(() => {
    const isUploading = uploadedDocuments.some((doc) => doc.isUploading);
    setIsUploading?.(isUploading);
  }, [uploadedDocuments]);

  return (
    <>
      {uploadedDocuments?.length > 0 && (
        <div className={`${mediaThumbnailClass}`}>
          {uploadedDocuments.map((document, index) => (
            <MediaThumbnail
              key={document.tempId || index}
              document={document}
              index={index}
              onDelete={handleDelete}
              onRetry={handleRetryUpload}
              deleteIcon={trashCan}
              retryIcon={retryArrow}
              setShowMediaViewer={setShowMediaViewer}
              allProcessed={allProcessed}
            />
          ))}
        </div>
      )}
      <div className={`${mediaButtonClass}`}>
        <label htmlFor="media">
          {uploadText} <span>{uploadIcon}</span>
        </label>
        <input
          type="file"
          id="media"
          name="media"
          aria-label="media"
          placeholder="Upload file here"
          onChange={handleSelectMedia}
          multiple={!!allowMultipleUpload}
          accept={
            Array.isArray(supportedFiles) && supportedFiles.length > 0
              ? supportedFiles.join(",")
              : undefined
          }
        />
      </div>
      {showMediaViewer && (
        <MediaViewer
          media={uploadedDocuments}
          setShowMediaViewer={setShowMediaViewer}
        />
      )}
    </>
  );
};
export default MediaUploader;
